import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { usePlayerContext } from '../../player/_contexts/player-context';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PageType,
  UrlParams,
  instantScrollBehaviorOnFocusKey,
  HERO_SCROLL_HEIGHT
} from '../_utils/types';
import { onUIElementFocus, animatedScroll } from '../_utils/utils';
import { setFocusToHomeAndScrollTop } from '@smart-tv/utils/focus';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import { useAtom, useSetAtom } from 'jotai';
import useStripesStore from '../_stores/useStripesStore';
import {
  pageAreaAtom,
  focusKeyPrevAtom,
  skipAutoFocusOnFrontpageAtom,
  topLevelPageReadyAtom
} from '@smart-tv/atoms';
import useStripesBase from '../_hooks/use-stripes-base';
import { getAreaForPageType } from '@smart-tv/utils/kmm/helpers';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

const useStripesRegular = (props: { pageType: PageType }) => {
  const { pageType } = props;
  const location = useLocation();
  const { universeTitle, universeId } = useParams() as UrlParams;
  const { player } = usePlayerContext();
  const navigate = useNavigate();
  const { selectedFavoritesList } = useFavoritesLists();
  const setPageAreaAtom = useSetAtom(pageAreaAtom);
  const setFocusKeyPrev = useSetAtom(focusKeyPrevAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const [skipAutoFocusOnFrontpage] = useAtom(skipAutoFocusOnFrontpageAtom);

  const stripesStore = useStripesStore({
    pageType: pageType,
    favoritesListId: selectedFavoritesList?.id ?? undefined,
    universeId: universeId
  });

  const {
    ref,
    focusKey,
    openDialog,
    openCollections,
    handleOpen,
    handleonShowAllEnterPress,
    savedFocusKeyBeforeError,
    savedUIElementBeforeError
  } = useStripesBase({ pageType, stripesStore });

  const shouldFocusFirstElement = useRef(
    pageType === PageType.Universe
      ? location.state?.headerPreview !== true
      : !skipAutoFocusOnFrontpage && location.state?.headerPreview !== true
  );

  const retryOpenCollection = useCallback(async () => {
    if (savedFocusKeyBeforeError.current) {
      setFocus(savedFocusKeyBeforeError.current);
    }
    if (savedUIElementBeforeError.current) {
      stripesStore.setActiveUIElement(savedUIElementBeforeError.current);
    }

    handleOpen();
  }, [stripesStore, handleOpen]);

  useEffect(() => {
    if (stripesStore.uiElements.length > 0) setTopLevelPageReady(true);
  }, [setTopLevelPageReady, stripesStore.uiElements.length]);

  useEffect(() => {
    if (
      pageType === PageType.FrontPage &&
      location.state?.focusFirst === true &&
      stripesStore.uiElements.length > 0
    ) {
      setFocus('0-0');
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate, pageType, stripesStore.uiElements.length]);

  useEffect(() => {
    const parsedUniverseId = parseInt(universeId);
    setPageAreaAtom(
      getAreaForPageType(
        pageType,
        isNaN(parsedUniverseId) ? undefined : parsedUniverseId,
        undefined
      )
    );
  }, [universeId, setPageAreaAtom, pageType]);

  useEffect(() => {
    if (pageType === PageType.Universe) {
      //reset focuskeyprev when opening the universe so that the focus is set to the first element in first stripe
      setFocusKeyPrev(null);
    }
  }, [pageType, setFocusKeyPrev]);

  const onHeroUIElementFocus = useCallback(({ y }: { y: number }) => {
    // Use instant scrolling on initial mount to avoid animating during component initialization
    if (shouldFocusFirstElement.current) {
      sessionStorage.setItem(instantScrollBehaviorOnFocusKey, 'true');
      shouldFocusFirstElement.current = false;
    }
    animatedScroll(y - HERO_SCROLL_HEIGHT);
  }, []);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (window.location.pathname.split('/').length > 2) {
        if (!player && !openDialog && !openCollections && isBackKey(event)) {
          navigate('/udforsk', { state: { universeTitle } });
        }
      }
      if (window.location.pathname === '/') {
        if (!player && !openDialog && !openCollections && isBackKey(event)) {
          setFocusToHomeAndScrollTop();
        }
      }
    },
    [navigate, openCollections, openDialog, player, universeTitle]
  );

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return {
    ref,
    focusKey,
    uiElements: stripesStore.uiElements,
    activeUIElement: stripesStore.activeUIElement,
    openCollections,
    handleonShowAllEnterPress,
    onUIElementFocus,
    onHeroUIElementFocus,
    universeTitle,
    error: stripesStore.error,
    retryOpenCollection,
    shouldFocusFirstElement: shouldFocusFirstElement.current
  };
};

export default useStripesRegular;
